import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Heart,
  Play,
  Trash2, 
  ArrowLeft,
  AlertCircle,
  Loader2,
  Clock,
  Calendar,
  Star,
  Eye,
  VideoIcon
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const WatchlistPage = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total_items: 0,
    total_duration: 0,
    categories: []
  });

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }

    loadWatchlist();
  }, [user, navigate]);

  const loadWatchlist = async () => {
    try {
      setLoading(true);
      setError(null);

      // Endpoint à créer dans le backend
      const response = await axios.get(`${API_BASE_URL}/watchlist/user/${user.uid}`);

      if (response.data.success) {
        setWatchlist(response.data.data.items || []);
        setStats(response.data.data.stats || {
          total_items: 0,
          total_duration: 0,
          categories: []
        });
      } else {
        throw new Error('Erreur récupération watchlist');
      }

    } catch (error) {
      console.error('Erreur chargement watchlist:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erreur inconnue';
      setError(errorMessage);
      
      // Si l'endpoint n'existe pas encore, utiliser des données mockées
      if (error.response?.status === 404) {
        setWatchlist([
          {
            id: '1',
            video_id: 'mock_1',
            video: {
              id: 'mock_1',
              title: 'Neural Dreams - Court-métrage IA',
              description: 'Une exploration visuelle de l\'inconscient collectif généré par intelligence artificielle.',
              thumbnail_url: 'https://picsum.photos/800/450?random=1',
              duration_seconds: 180,
              status: 'published',
              created_at: '2025-01-10T14:30:00Z',
              views: 1250,
              user: {
                username: 'CreativeAI_Studio'
              }
            },
            added_at: '2025-01-11T10:00:00Z'
          },
          {
            id: '2',
            video_id: 'mock_2',
            video: {
              id: 'mock_2',
              title: 'Cyberpunk Metropolis',
              description: 'Vision futuriste d\'une métropole cyberpunk créée par IA générative.',
              thumbnail_url: 'https://picsum.photos/800/450?random=2',
              duration_seconds: 240,
              status: 'published',
              created_at: '2025-01-09T16:20:00Z',
              views: 890,
              user: {
                username: 'FutureTech_AI'
              }
            },
            added_at: '2025-01-10T15:30:00Z'
          }
        ]);
        setStats({
          total_items: 2,
          total_duration: 420, // 7 minutes
          categories: ['Sci-Fi Drama', 'Cyberpunk']
        });
        setError(null);
      } else {
        toast({
          title: "Erreur",
          description: `Impossible de charger votre watchlist: ${errorMessage}`,
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const removeFromWatchlist = async (watchlistId, videoTitle) => {
    if (!window.confirm(`Retirer "${videoTitle}" de votre watchlist ?`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE_URL}/watchlist/${watchlistId}`);
      
      if (response.data.success) {
        setWatchlist(prev => prev.filter(item => item.id !== watchlistId));
        
        toast({
          title: "Retiré de la watchlist",
          description: `"${videoTitle}" a été retiré de votre watchlist`
        });
        
        // Recalculer les stats
        const newTotal = watchlist.length - 1;
        const newDuration = watchlist
          .filter(item => item.id !== watchlistId)
          .reduce((sum, item) => sum + (item.video.duration_seconds || 0), 0);
        
        setStats(prev => ({
          ...prev,
          total_items: newTotal,
          total_duration: newDuration
        }));
      }
    } catch (error) {
      console.error('Erreur suppression watchlist:', error);
      
      // Pour la démo, supprimer quand même de l'état local
      setWatchlist(prev => prev.filter(item => item.id !== watchlistId));
      
      toast({
        title: "Retiré (mode démo)",
        description: `"${videoTitle}" retiré localement (API pas encore implémentée)`
      });
    }
  };

  const playVideo = (video) => {
    // Rediriger vers la page de détail vidéo
    navigate(`/details/${video.id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non défini';
    
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Date invalide';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return 'Non défini';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatTotalDuration = (totalSeconds) => {
    if (!totalSeconds) return '0 min';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    } else {
      return `${minutes}min`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader2 className="h-12 w-12 text-pink-400 mx-auto animate-spin mb-4" />
              <p className="text-gray-300">Chargement de votre watchlist...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-8">
          <Button
            onClick={() => navigate('/home')}
            variant="outline"
            size="sm"
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Retour</span>
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center space-x-3">
              <Heart className="h-8 w-8 text-pink-400 fill-pink-400" />
              <span>Ma Watchlist</span>
            </h1>
            <p className="text-gray-400 mt-1">Vos vidéos favorites à regarder plus tard</p>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-r from-pink-600/20 to-pink-700/20 border border-pink-500/30 rounded-2xl p-6">
            <div className="flex items-center space-x-3">
              <VideoIcon className="h-8 w-8 text-pink-400" />
              <div>
                <h3 className="text-2xl font-bold text-white">{stats.total_items}</h3>
                <p className="text-pink-200 text-sm">Vidéos sauvegardées</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-violet-600/20 to-violet-700/20 border border-violet-500/30 rounded-2xl p-6">
            <div className="flex items-center space-x-3">
              <Clock className="h-8 w-8 text-violet-400" />
              <div>
                <h3 className="text-2xl font-bold text-white">{formatTotalDuration(stats.total_duration)}</h3>
                <p className="text-violet-200 text-sm">Temps de visionnage</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-600/20 to-blue-700/20 border border-blue-500/30 rounded-2xl p-6">
            <div className="flex items-center space-x-3">
              <Star className="h-8 w-8 text-blue-400" />
              <div>
                <h3 className="text-2xl font-bold text-white">{stats.categories?.length || 0}</h3>
                <p className="text-blue-200 text-sm">Catégories différentes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Liste des vidéos */}
        {error && (
          <div className="bg-red-900/20 border border-red-600/30 rounded-2xl p-6 mb-8">
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-6 w-6 text-red-400" />
              <div>
                <h3 className="text-red-200 font-semibold">Mode démo actif</h3>
                <p className="text-red-300 text-sm">API watchlist pas encore connectée - affichage données de démonstration</p>
              </div>
            </div>
          </div>
        )}

        {watchlist.length > 0 ? (
          <div className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-lg border border-gray-700 rounded-2xl p-8">
            <h2 className="text-xl font-semibold text-white mb-6">Vos vidéos favorites ({watchlist.length})</h2>
            
            <div className="grid gap-6">
              {watchlist.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col lg:flex-row items-start lg:items-center space-y-4 lg:space-y-0 lg:space-x-6 p-6 bg-gray-800/50 rounded-xl hover:bg-gray-700/50 transition-colors border border-gray-600/50"
                >
                  {/* Thumbnail */}
                  <div className="w-full lg:w-48 aspect-video bg-gray-700 rounded-lg overflow-hidden flex-shrink-0 relative group cursor-pointer">
                    {item.video.thumbnail_url ? (
                      <img
                        src={item.video.thumbnail_url}
                        alt={item.video.title}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        onClick={() => playVideo(item.video)}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <VideoIcon className="h-12 w-12" />
                      </div>
                    )}
                    
                    {/* Play overlay */}
                    <div 
                      className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      onClick={() => playVideo(item.video)}
                    >
                      <Play className="h-12 w-12 text-white fill-white" />
                    </div>

                    {/* Durée */}
                    {item.video.duration_seconds && (
                      <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
                        {formatDuration(item.video.duration_seconds)}
                      </div>
                    )}
                  </div>

                  {/* Informations */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-xl font-semibold text-white truncate mr-4">
                        {item.video.title}
                      </h3>
                      <Badge 
                        variant={item.video.status === 'published' ? 'default' : 'secondary'} 
                        className={item.video.status === 'published' ? 'bg-green-600' : 'bg-yellow-600'}
                      >
                        {item.video.status === 'published' ? 'Prêt' : 'En traitement'}
                      </Badge>
                    </div>
                    
                    <p className="text-gray-300 text-sm line-clamp-2 mb-3">
                      {item.video.description}
                    </p>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>Ajouté le {formatDate(item.added_at)}</span>
                      </div>
                      {item.video.user && (
                        <div className="flex items-center space-x-1">
                          <span>Par {item.video.user.username}</span>
                        </div>
                      )}
                      {item.video.views && (
                        <div className="flex items-center space-x-1">
                          <Eye className="h-4 w-4" />
                          <span>{item.video.views} vues</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      onClick={() => playVideo(item.video)}
                      className="bg-pink-600 hover:bg-pink-700 flex items-center space-x-1"
                    >
                      <Play className="h-4 w-4" />
                      <span className="hidden sm:inline">Regarder</span>
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removeFromWatchlist(item.id, item.video.title)}
                      className="flex items-center space-x-1 hover:bg-red-600/20 hover:border-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="hidden sm:inline">Retirer</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-lg border border-gray-700 rounded-2xl p-12 text-center">
            <Heart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Watchlist vide</h3>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Vous n'avez pas encore ajouté de vidéos à votre watchlist. 
              Explorez notre contenu et ajoutez vos créations IA favorites !
            </p>
            <Button
              onClick={() => navigate('/home')}
              className="bg-gradient-to-r from-pink-600 to-violet-600 hover:from-pink-700 hover:to-violet-700"
            >
              <VideoIcon className="h-5 w-5 mr-2" />
              Découvrir des vidéos
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WatchlistPage;